/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('facet-controller')) return;

  var AirX = window.AirX || {};

  function motionOn() {
    return window.gsap && document.documentElement.classList.contains('motion');
  }

  function prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function shuffleSeed() {
    var seed;
    try {
      seed = sessionStorage.getItem('airx:shuffle-seed');
      if (!seed) {
        seed = String(1 + Math.floor(Math.random() * 2147483646));
        sessionStorage.setItem('airx:shuffle-seed', seed);
      }
    } catch (err) {
      seed = '873190441';
    }
    return parseInt(seed, 10) || 873190441;
  }

  function shuffleSlots(children, salt) {
    var rng = mulberry32(shuffleSeed() + (salt || 0) * 7919);
    var indexes = [];
    children.forEach(function (el, i) {
      if (el.hasAttribute && el.hasAttribute('data-slot-product')) indexes.push(i);
    });
    var products = indexes.map(function (i) { return children[i]; });
    for (var i = products.length - 1; i > 0; i--) {
      var j = Math.floor(rng() * (i + 1));
      var tmp = products[i];
      products[i] = products[j];
      products[j] = tmp;
    }
    indexes.forEach(function (pos, k) { children[pos] = products[k]; });
    return children;
  }

  if (AirX.DrawerElement && !customElements.get('facet-drawer')) {
    customElements.define('facet-drawer', class FacetDrawer extends AirX.DrawerElement {});
  }

  class SortSelect extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sort-button]');
      this.list = this.querySelector('[data-sort-list]');
      this.current = this.querySelector('[data-sort-current]');
      this.native = this.querySelector('select');
      if (!this.button || !this.list) return;
      this.options = Array.from(this.list.querySelectorAll('[role="option"]'));

      this.button.addEventListener('click', () => this.toggle());
      this.button.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.open();
        }
      });
      this.list.addEventListener('keydown', (e) => this.onListKeydown(e));
      this.options.forEach((opt) => {
        opt.addEventListener('click', () => this.select(opt));
      });
      this.onDocumentClick = (e) => {
        if (this.isOpen && !this.contains(e.target)) this.close(false);
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get isOpen() {
      return this.list && !this.list.hidden;
    }

    get value() {
      var selected = this.options.find((o) => o.getAttribute('aria-selected') === 'true');
      return selected ? selected.dataset.value : this.dataset.defaultSort || '';
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    open() {
      if (this.isOpen) return;
      this.list.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      if (motionOn()) {
        window.gsap.fromTo(
          this.list,
          { opacity: 0, y: 8 },
          { opacity: 1, y: 0, duration: 0.3, ease: 'power3.out', clearProps: 'opacity,transform' }
        );
      }
      var active = this.options.find((o) => o.getAttribute('aria-selected') === 'true') || this.options[0];
      this.focusOption(active);
    }

    close(refocus) {
      if (!this.isOpen) return;
      this.list.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      if (refocus !== false) this.button.focus();
    }

    focusOption(option) {
      if (!option) return;
      this.options.forEach((o) => (o.tabIndex = -1));
      option.tabIndex = 0;
      option.focus();
    }

    onListKeydown(e) {
      var index = this.options.indexOf(document.activeElement);
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.focusOption(this.options[Math.min(index + 1, this.options.length - 1)]);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.focusOption(this.options[Math.max(index - 1, 0)]);
          break;
        case 'Home':
          e.preventDefault();
          this.focusOption(this.options[0]);
          break;
        case 'End':
          e.preventDefault();
          this.focusOption(this.options[this.options.length - 1]);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (index > -1) this.select(this.options[index]);
          break;
        case 'Escape':
          e.preventDefault();
          this.close();
          break;
        case 'Tab':
          this.close(false);
          break;
      }
    }

    select(option) {
      this.setValue(option.dataset.value);
      this.close();
      this.dispatchEvent(
        new CustomEvent('sort:change', { bubbles: true, detail: { value: option.dataset.value } })
      );
    }

    setValue(value) {
      this.options.forEach((o) => {
        var selected = o.dataset.value === value;
        o.setAttribute('aria-selected', String(selected));
        if (selected && this.current) {
          var label = o.querySelector('.sort-select__option-label');
          this.current.textContent = label ? label.textContent : o.textContent.trim();
        }
      });
      if (this.native) this.native.value = value;
    }
  }
  if (!customElements.get('sort-select')) customElements.define('sort-select', SortSelect);

  var PRESERVED_PARAMS = ['q', 'type', 'options[prefix]'];

  class FacetController extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.baseUrl = this.dataset.baseUrl || window.location.pathname;
      this.defaultSort = this.dataset.defaultSort || '';
      this.form = this.querySelector('[data-facets-form]');
      this.gridContainer = this.querySelector('#ProductGridContainer') || this.querySelector('[data-grid-container]');
      this.loader = this.querySelector('[data-facets-loader]');
      this.live = this.querySelector('[data-facets-live]');
      this.errorEl = this.querySelector('[data-facets-error]');
      this.renderToken = 0;

      if (this.form) {
        this.debouncedApply = (AirX.debounce || this.fallbackDebounce)(() => this.applyFilters(), 300);
        this.form.addEventListener('change', () => this.debouncedApply());
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.applyFilters();
          this.closeDrawer();
        });
      }

      this.addEventListener('sort:change', (e) => {
        this.setSortValue(e.detail.value);
        this.applyFilters();
      });

      this.addEventListener('click', (e) => {
        var remove = e.target.closest('[data-facet-remove]');
        if (remove) {
          e.preventDefault();
          this.onRemoveFacet(remove);
          return;
        }
        var loadMore = e.target.closest('[data-load-more]');
        if (loadMore) {
          e.preventDefault();
          this.onLoadMore(loadMore);
        }
      });

      if (AirX.on) {
        this.onDrawerOpen = (e) => this.syncFilterButton(e, true);
        this.onDrawerClose = (e) => this.syncFilterButton(e, false);
        AirX.on('drawer:open', this.onDrawerOpen);
        AirX.on('drawer:close', this.onDrawerClose);
      }

      this.onPopstate = () => {
        this.renderPage(new URLSearchParams(window.location.search), { push: false, scroll: false });
      };
      window.addEventListener('popstate', this.onPopstate);

      if (this.hasAttribute('data-shuffle')) this.shuffleGrid(0);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopstate);
      if (AirX.off) {
        AirX.off('drawer:open', this.onDrawerOpen);
        AirX.off('drawer:close', this.onDrawerClose);
      }
    }

    fallbackDebounce(fn, wait) {
      var t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    }

    closeDrawer() {
      var drawer = document.getElementById('FacetDrawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
    }

    syncFilterButton(e, open) {
      if (!e.detail || e.detail.id !== 'FacetDrawer') return;
      this.querySelectorAll('[data-facets-open]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', String(open));
      });
    }

    setSortValue(value) {
      var input = this.querySelector('[data-facets-sort-input]');
      if (input) input.value = value;
      var sortEl = this.querySelector('sort-select');
      if (sortEl && sortEl.setValue) sortEl.setValue(value);
    }

    buildParams() {
      var params = new URLSearchParams();
      if (this.form) {
        var formData = new FormData(this.form);
        formData.forEach(function (value, key) {
          if (String(value).trim() !== '') params.append(key, value);
        });
      } else {
        var current = new URLSearchParams(window.location.search);
        PRESERVED_PARAMS.forEach(function (key) {
          if (current.get(key)) params.set(key, current.get(key));
        });
        var sortEl = this.querySelector('sort-select');
        if (sortEl) params.set('sort_by', sortEl.value);
      }
      if (params.get('sort_by') === this.defaultSort) params.delete('sort_by');
      params.delete('page');
      return params;
    }

    applyFilters() {
      this.renderPage(this.buildParams(), {});
    }

    onRemoveFacet(link) {
      var url = new URL(link.getAttribute('href'), window.location.origin);
      var params = url.searchParams;
      params.delete('page');
      this.renderPage(params, {});
    }

    setLoading(loading) {
      if (this.gridContainer) {
        this.gridContainer.classList.toggle('is-loading', loading);
        this.gridContainer.setAttribute('aria-busy', String(loading));
      }
      if (this.loader) this.loader.classList.toggle('is-active', loading);
    }

    async renderPage(params, opts) {
      opts = opts || {};
      var token = ++this.renderToken;
      var qs = params.toString();
      var pageUrl = this.baseUrl + (qs ? '?' + qs : '');
      var fetchUrl = pageUrl + (qs ? '&' : '?') + 'section_id=' + encodeURIComponent(this.sectionId);

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        var res = await fetch(fetchUrl, { signal: this.abortController.signal });
        if (!res.ok) throw new Error(res.statusText);
        var text = await res.text();
        if (token !== this.renderToken) return;
        var doc = new DOMParser().parseFromString(text, 'text/html');
        this.swapResults(doc, params);
        if (opts.push !== false) {
          window.history.pushState({ airxFacets: true }, '', pageUrl);
        }
        if (opts.scroll !== false) this.scrollToResults();
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        if (this.errorEl) this.errorEl.hidden = false;
      } finally {
        if (token === this.renderToken) this.setLoading(false);
      }
    }

    swapResults(doc, params) {

      var focused = null;
      var active = document.activeElement;
      if (this.form && active && this.form.contains(active) && active.name) {
        focused = { name: active.name, value: active.value };
      }

      var freshContainer = doc.querySelector('#ProductGridContainer') || doc.querySelector('[data-grid-container]');
      if (freshContainer && this.gridContainer) {
        this.gridContainer.innerHTML = freshContainer.innerHTML;
        if (this.hasAttribute('data-shuffle') && !params.get('sort_by')) this.shuffleGrid(0);
        this.animateGrid(this.gridContainer.querySelectorAll('[data-product-grid] > *'));
      }

      this.querySelectorAll('[data-active-facets]').forEach(function (wrap) {
        var fresh = doc.querySelector('[data-active-facets]');
        if (fresh) wrap.innerHTML = fresh.innerHTML;
      });
      var freshInner = doc.querySelector('[data-active-count-value]');
      var count = freshInner ? parseInt(freshInner.getAttribute('data-active-count-value'), 10) || 0 : 0;
      this.querySelectorAll('[data-active-count]').forEach(function (chip) {
        chip.textContent = count;
        chip.classList.toggle('is-hidden', count === 0);
      });

      var freshCount = doc.querySelector('[data-results-count]');
      if (freshCount) {
        var countText = freshCount.textContent.trim();
        this.querySelectorAll('[data-results-count]').forEach(function (el) {
          el.textContent = countText;
        });
        if (this.live) this.live.textContent = countText;
      }
      var freshApply = doc.querySelector('[data-apply-label]');
      var applyLabel = this.querySelector('[data-apply-label]');
      if (freshApply && applyLabel) applyLabel.textContent = freshApply.textContent.trim();

      this.updateFacetGroups(doc);

      this.setSortValue(params.get('sort_by') || this.defaultSort);

      if (focused && this.form) {
        var selector =
          'input[name="' + CSS.escape(focused.name) + '"][value="' + CSS.escape(focused.value) + '"],' +
          'input[name="' + CSS.escape(focused.name) + '"]:not([type="checkbox"])';
        var again = this.form.querySelector(selector);
        if (again) again.focus({ preventScroll: true });
      }

      if (AirX.emit) AirX.emit('motion:scan', { root: this.gridContainer });
    }

    updateFacetGroups(doc) {
      if (!this.form) return;
      this.form.querySelectorAll('[data-facet-param]').forEach(function (group) {
        var param = group.getAttribute('data-facet-param');
        var fresh = null;
        doc.querySelectorAll('[data-facet-param]').forEach(function (candidate) {
          if (!fresh && candidate.getAttribute('data-facet-param') === param) fresh = candidate;
        });
        if (!fresh) {
          group.hidden = true;
          return;
        }
        group.hidden = false;

        var values = group.querySelector('[data-facet-values]');
        var freshValues = fresh.querySelector('[data-facet-values]');
        if (values && freshValues) values.innerHTML = freshValues.innerHTML;

        var meta = group.querySelector('[data-facet-selected]');
        var freshMeta = fresh.querySelector('[data-facet-selected]');
        if (meta && freshMeta) meta.textContent = freshMeta.textContent.trim();

        group.querySelectorAll('input[type="number"]').forEach(function (input) {
          if (input === document.activeElement) return;
          var freshInput = fresh.querySelector('input[name="' + CSS.escape(input.name) + '"]');
          if (freshInput) input.value = freshInput.value;
        });
      });
    }

    animateGrid(items) {
      if (!items || !items.length || !motionOn() || prefersReduced()) return;
      window.gsap.fromTo(
        items,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.045,
          overwrite: true,
          clearProps: 'opacity,transform',
        }
      );
    }

    scrollToResults() {
      if (!this.gridContainer) return;
      var rect = this.gridContainer.getBoundingClientRect();
      var offset = 150;
      if (rect.top < 0) {
        window.scrollTo({
          top: window.scrollY + rect.top - offset,
          behavior: prefersReduced() ? 'auto' : 'smooth',
        });
      }
    }

    async onLoadMore(link) {
      if (link.getAttribute('aria-disabled') === 'true') return;
      link.classList.add('btn--loading');
      link.setAttribute('aria-disabled', 'true');

      var url = new URL(link.href, window.location.origin);
      url.hash = '';
      var pageParam = url.searchParams.get('page');
      url.searchParams.set('section_id', this.sectionId);

      try {
        var res = await fetch(url.href);
        if (!res.ok) throw new Error(res.statusText);
        var text = await res.text();
        var doc = new DOMParser().parseFromString(text, 'text/html');

        var grid = this.querySelector('[data-product-grid]');
        var freshGrid = doc.querySelector('[data-product-grid]');
        if (!grid || !freshGrid) throw new Error('missing grid');

        var fresh = Array.from(freshGrid.children);
        if (this.hasAttribute('data-shuffle') && !url.searchParams.get('sort_by')) {
          fresh = shuffleSlots(fresh, parseInt(pageParam, 10) || 0);
        }
        fresh.forEach(function (item) { grid.appendChild(item); });

        var pagination = this.querySelector('[data-pagination]');
        var freshPagination = doc.querySelector('[data-pagination]');
        if (pagination && freshPagination) {
          pagination.replaceWith(freshPagination);
        } else if (pagination) {
          pagination.remove();
        }

        if (pageParam) {
          var current = new URLSearchParams(window.location.search);
          current.set('page', pageParam);
          window.history.replaceState({ airxFacets: true }, '', this.baseUrl + '?' + current.toString());
        }

        this.animateGrid(fresh);
        if (AirX.emit) AirX.emit('motion:scan', { root: this.gridContainer });

        var nextTrigger = this.querySelector('[data-load-more]');
        if (nextTrigger) {
          nextTrigger.focus({ preventScroll: true });
        } else {
          var firstNew = fresh[0] && fresh[0].querySelector('a');
          if (firstNew) firstNew.focus({ preventScroll: true });
        }
      } catch (err) {

        window.location.href = link.href;
      } finally {
        link.classList.remove('btn--loading');
        link.removeAttribute('aria-disabled');
      }
    }

    shuffleGrid(salt) {
      if (new URLSearchParams(window.location.search).get('sort_by')) return;
      var grid = this.querySelector('[data-product-grid]');
      if (!grid || grid.children.length < 3) return;
      var children = shuffleSlots(Array.from(grid.children), salt);
      children.forEach(function (el) { grid.appendChild(el); });
    }
  }

  customElements.define('facet-controller', FacetController);
})();
